import {
  AppBar,
  makeStyles,
  Tab as TabComponent,
  Tabs,
  TabsProps,
} from '@material-ui/core';
import * as React from 'react';
import { useLocation } from 'react-router';

interface Tab<T extends string> {
  label: string;
  value: T;
}

interface Props<T extends string> extends Omit<TabsProps, 'value'> {
  tabs: Tab<T>[];
  defaultTab?: T;

  children(activeTab: T): React.ReactNode;
}

const useStyles = makeStyles((theme) => ({
  appBar: {
    marginBottom: theme.spacing(2),
  },
}));

export function AppTabs<T extends string>({
  tabs,
  defaultTab,
  children,
  onChange: externalOnChange,
  ...tabsProps
}: Props<T>) {
  const classes = useStyles();
  const { hash } = useLocation();
  const activeTab = getTabForHash(hash);

  const handleChange = (_: React.ChangeEvent<{}>, newValue: T) => {
    window.location.hash = newValue;
    if (externalOnChange) {
      externalOnChange(_, newValue);
    }
  };

  return (
    <React.Fragment>
      <AppBar
        position="static"
        color="default"
        elevation={1}
        className={classes.appBar}
      >
        <Tabs
          value={activeTab}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          onChange={handleChange}
          {...tabsProps}
        >
          {tabs.map(({ label, value }) => (
            <TabComponent label={label} value={value} key={value} />
          ))}
        </Tabs>
      </AppBar>

      {children(activeTab)}
    </React.Fragment>
  );

  function getTabForHash(h: string): T {
    const hashes = h.split('#');
    const fragment = (hashes.length > 1 ? hashes[1] : h).replace('#', '');

    if (isTab(fragment)) {
      return fragment;
    }

    if (defaultTab) {
      return defaultTab;
    }

    if (tabs.length > 0) {
      return tabs[0].value;
    }

    throw new Error(`Unknown tab for hash ${h}`);
  }

  function isTab(tab: string): tab is T {
    return tabs.some(({ value }) => value === tab);
  }
}
